/**
 * Modal para exibir detalhes das unidades com uma pendência específica
 * Exibe tabela com pesquisa e ordenação
 */
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiFetch, getApiUrl } from '@/lib/api';

// Tipos
interface PendencyDetailItem {
  documentGuid: string;
  unitIdentification: string;
  unitName: string;
  regional: string;
  cnpj: string;
  address: string;
  pendencyType: string;
  supplier: string;
  documentType: string;
}

interface PendencyDetailsResponse {
  status: string;
  result: {
    pendencyType: string;
    total: number;
    items: PendencyDetailItem[];
  };
}

interface PendencyDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendencyType: string;
  filters?: {
    regional?: string;
    uf?: string;
    docTypes?: string;
    suppliers?: string;
  };
}

type SortField = 'unitIdentification' | 'regional' | 'cnpj' | 'address' | 'supplier';
type SortDirection = 'asc' | 'desc';

// Helper para construir query string
function buildQueryString(params: Record<string, string | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(v!)}`).join('&');
}

export function PendencyDetailsModal({
  open,
  onOpenChange,
  pendencyType,
  filters = {},
}: PendencyDetailsModalProps) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('unitIdentification');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Buscar dados
  const { data, isLoading, error } = useQuery({
    queryKey: ['pendency-details', pendencyType, filters],
    queryFn: () => {
      const qs = buildQueryString({
        pendencyType,
        regional: filters.regional,
        uf: filters.uf,
        docTypes: filters.docTypes,
        suppliers: filters.suppliers,
      });
      return apiFetch<PendencyDetailsResponse>(`/claro/api/processes/pendency-details${qs}`);
    },
    enabled: open && !!pendencyType,
    staleTime: 30 * 1000, // 30 segundos
  });

  const items = data?.result?.items || [];

  // Filtrar e ordenar
  const filteredAndSorted = useMemo(() => {
    let result = [...items];

    // Filtrar por pesquisa
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      result = result.filter(
        (item) =>
          item.unitIdentification.toLowerCase().includes(searchLower) ||
          item.unitName.toLowerCase().includes(searchLower) ||
          item.regional.toLowerCase().includes(searchLower) ||
          item.cnpj.toLowerCase().includes(searchLower) ||
          item.address.toLowerCase().includes(searchLower) ||
          item.supplier.toLowerCase().includes(searchLower)
      );
    }

    // Ordenar
    result.sort((a, b) => {
      const aVal = a[sortField] || '';
      const bVal = b[sortField] || '';
      const cmp = aVal.localeCompare(bVal, 'pt-BR');
      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return result;
  }, [items, search, sortField, sortDirection]);

  // Handler para ordenação
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Render do ícone de ordenação
  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] md:max-w-[90vw] lg:max-w-[1200px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span>
              Unidades com Pendência: <span className="text-primary">{pendencyType}</span>
            </span>
            <span className="text-sm font-normal text-muted-foreground">
              {filteredAndSorted.length} de {items.length} registros
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Campo de pesquisa */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por ID, nome, regional, CNPJ, endereço ou fornecedor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9"
          />
          {search && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
              onClick={() => setSearch('')}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* Tabela */}
        <div className="flex-1 overflow-auto border rounded-lg">
          {isLoading ? (
            <div className="flex items-center justify-center h-[300px]">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-[300px] text-red-500">
              Erro ao carregar dados: {error instanceof Error ? error.message : 'Erro desconhecido'}
            </div>
          ) : filteredAndSorted.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-muted-foreground">
              {search ? 'Nenhum resultado encontrado para a pesquisa' : 'Nenhuma unidade encontrada'}
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky top-0 bg-background z-10">
                <TableRow>
                  <TableHead className="w-[120px]">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 -ml-2 font-semibold"
                      onClick={() => handleSort('unitIdentification')}
                    >
                      ID Unidade
                      <SortIcon field="unitIdentification" />
                    </Button>
                  </TableHead>
                  <TableHead className="w-[120px]">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 -ml-2 font-semibold"
                      onClick={() => handleSort('regional')}
                    >
                      Regional
                      <SortIcon field="regional" />
                    </Button>
                  </TableHead>
                  <TableHead className="w-[160px]">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 -ml-2 font-semibold"
                      onClick={() => handleSort('cnpj')}
                    >
                      CNPJ
                      <SortIcon field="cnpj" />
                    </Button>
                  </TableHead>
                  <TableHead className="min-w-[250px]">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 -ml-2 font-semibold"
                      onClick={() => handleSort('address')}
                    >
                      Endereço
                      <SortIcon field="address" />
                    </Button>
                  </TableHead>
                  <TableHead className="w-[200px]">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 -ml-2 font-semibold"
                      onClick={() => handleSort('supplier')}
                    >
                      Fornecedor
                      <SortIcon field="supplier" />
                    </Button>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSorted.map((item, index) => (
                  <TableRow key={`${item.documentGuid}-${index}`}>
                    <TableCell className="font-mono text-sm">
                      {item.unitIdentification || '-'}
                    </TableCell>
                    <TableCell>{item.regional || '-'}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {item.cnpj ? formatCNPJ(item.cnpj) : '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.address || '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {item.supplier || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper para formatar CNPJ
function formatCNPJ(cnpj: string): string {
  // Remove caracteres não numéricos
  const numbers = cnpj.replace(/\D/g, '');
  if (numbers.length !== 14) return cnpj;
  
  // Formata: XX.XXX.XXX/XXXX-XX
  return numbers.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    '$1.$2.$3/$4-$5'
  );
}
